import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage} from 'react-intl';

import styles from './save-to-account.css';
import saveIcon from './icon--save.svg';

/*
 * The always-visible "save to my account" button in the menu bar.
 *
 * Saving used to be reachable only through the account menu, which made the
 * feature hard to find. This button sits next to the account name so the
 * primary action is one click away.
 *
 * It reports three states so the user always knows where the work stands:
 *   - saving:  the request is in flight
 *   - saved:   briefly confirms success after a save
 *   - idle:    ready to save
 *
 * Each state is a separate <FormattedMessage> because react-intl requires
 * message ids to be statically analysable at build time.
 */
const SaveToAccount = props => {
    const {isSaving, savedAt, ...rest} = props;

    // The confirmation is transient: it only means something right after the
    // click, so it fades back to the normal label.
    const showSaved = !isSaving && savedAt && (Date.now() - savedAt) < 3000;

    return (
        <div
            {...rest}
            className={classNames(styles.saveButton, {
                [styles.saving]: isSaving,
                [styles.saved]: showSaved
            })}
            id="account-save-button"
            role="button"
            tabIndex="0"
        >
            <img
                className={styles.saveIcon}
                src={saveIcon}
            />
            {isSaving ? (
                <FormattedMessage
                    defaultMessage="Saving..."
                    description="Label of the save button while a save is in flight"
                    id="gui.menuBar.savingToAccount"
                />
            ) : (showSaved ? (
                <FormattedMessage
                    defaultMessage="Saved"
                    description="Label shown briefly after saving to the account"
                    id="gui.menuBar.savedToAccount"
                />
            ) : (
                <FormattedMessage
                    defaultMessage="Save"
                    description="Label of the button that saves the project to the user account"
                    id="gui.menuBar.saveToAccount"
                />
            ))}
        </div>
    );
};

SaveToAccount.propTypes = {
    isSaving: PropTypes.bool,
    savedAt: PropTypes.number
};

export default SaveToAccount;
